/**
 * FeedbackForm - In-app bug report / feedback form
 *
 * Auto-captures:
 *  - Recent console logs, errors, and warnings (ring buffer)
 *  - Device & browser info
 *  - Current page URL
 *
 * Optional:
 *  - User-attached screenshot (paste or file upload)
 *
 * Submits to POST /api/submitFeedback
 */
import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import html2canvas from 'html2canvas';
import { getAuthHeaders } from '../../services/apiAuth';
import { getApiBaseUrl } from '../../utils/apiConfig';
import { auth } from '../../services/firebaseConfig';

/* ------------------------------------------------------------------ */
/*  Console log ring buffer — captures logs/errors/warnings in background  */
/* ------------------------------------------------------------------ */
const MAX_LOGS = 200;
const logBuffer: string[] = [];

/** Set up global console intercept once (guarded to survive HMR) */
const INTERCEPT_KEY = '__feedbackInterceptInstalled';
function installConsoleIntercept() {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const w = window as any;
  if (w[INTERCEPT_KEY]) return;
  w[INTERCEPT_KEY] = true;

  const origError = console.error;
  const origWarn = console.warn;
  const origLog = console.log;

  console.error = (...args: unknown[]) => {
    pushLog('ERROR', args);
    origError.apply(console, args);
  };
  console.warn = (...args: unknown[]) => {
    pushLog('WARN', args);
    origWarn.apply(console, args);
  };
  console.log = (...args: unknown[]) => {
    pushLog('LOG', args);
    origLog.apply(console, args);
  };

  window.addEventListener('error', (ev) => {
    pushLog('UNCAUGHT', [`${ev.message} at ${ev.filename}:${ev.lineno}:${ev.colno}`]);
  });
  window.addEventListener('unhandledrejection', (ev) => {
    pushLog('UNHANDLED_PROMISE', [String(ev.reason)]);
  });
}

/** Scrub sensitive patterns (tokens, keys, emails) before persisting */
const SENSITIVE_PATTERNS = [
  /Bearer\s+[A-Za-z0-9\-._~+/]+=*/gi,
  /AIza[A-Za-z0-9\-_]{35}/g,                // Firebase API keys
  /eyJ[A-Za-z0-9\-_]+\.eyJ[A-Za-z0-9\-_]+\.[A-Za-z0-9\-_]+/g, // JWT tokens (header.payload.signature)
  /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/g, // emails
];

function redact(text: string): string {
  let result = text;
  for (const pattern of SENSITIVE_PATTERNS) {
    result = result.replace(pattern, '[REDACTED]');
  }
  return result;
}

function pushLog(level: string, args: unknown[]) {
  const ts = new Date().toISOString();
  const msg = args.map(a => {
    if (a instanceof Error) return `${a.name}: ${a.message}${a.stack ? '\n' + a.stack : ''}`;
    if (typeof a === 'object') try { return JSON.stringify(a); } catch { return String(a); }
    return String(a);
  }).join(' ');
  logBuffer.push(`[${ts}] ${level}: ${redact(msg)}`);
  if (logBuffer.length > MAX_LOGS) logBuffer.shift();
}

// Install intercept immediately on module load
installConsoleIntercept();

/* ------------------------------------------------------------------ */
/*  API call ring buffer — captures recent fetch calls with status     */
/* ------------------------------------------------------------------ */
interface ApiCallEntry {
  ts: string;
  method: string;
  url: string;
  status: number | null;
  durationMs: number;
  error?: string;
}
const MAX_API_CALLS = 20;
const apiCallBuffer: ApiCallEntry[] = [];

const API_INTERCEPT_KEY = '__feedbackApiInterceptInstalled';
function installApiIntercept() {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const w = window as any;
  if (w[API_INTERCEPT_KEY]) return;
  w[API_INTERCEPT_KEY] = true;

  const origFetch = window.fetch.bind(window);
  window.fetch = async (input, init) => {
    const url = typeof input === 'string' ? input : input instanceof URL ? input.toString() : (input as Request).url;
    // Only log /api calls (skip external CDN / analytics)
    if (!url.includes('/api/')) return origFetch(input, init);

    const requestMethod = input instanceof Request ? input.method : undefined;
    const method = init?.method ?? requestMethod ?? 'GET';
    const start = Date.now();
    try {
      const resp = await origFetch(input, init);
      apiCallBuffer.push({
        ts: new Date().toISOString(),
        method, url: redact(url),
        status: resp.status,
        durationMs: Date.now() - start,
      });
      if (apiCallBuffer.length > MAX_API_CALLS) apiCallBuffer.shift();
      return resp;
    } catch (err) {
      apiCallBuffer.push({
        ts: new Date().toISOString(),
        method, url: redact(url),
        status: null,
        durationMs: Date.now() - start,
        error: err instanceof Error ? err.message : String(err),
      });
      if (apiCallBuffer.length > MAX_API_CALLS) apiCallBuffer.shift();
      throw err;
    }
  };
}
installApiIntercept();

/* ------------------------------------------------------------------ */
/*  Types                                                             */
/* ------------------------------------------------------------------ */
type FeedbackCategory = 'bug' | 'question' | 'payment' | 'feature' | 'other';

interface FeedbackFormProps {
  onClose: () => void;
  onSuccess?: () => void;
  recentMessages?: { sender: string; text: string; timestamp?: string }[];
}

const CATEGORIES: { value: FeedbackCategory; labelKey: string; icon: string }[] = [
  { value: 'bug', labelKey: 'feedback.categoryBug', icon: '🐛' },
  { value: 'payment', labelKey: 'feedback.categoryPayment', icon: '💳' },
  { value: 'question', labelKey: 'feedback.categoryQuestion', icon: '❓' },
  { value: 'feature', labelKey: 'feedback.categoryFeature', icon: '💡' },
  { value: 'other', labelKey: 'feedback.categoryOther', icon: '📝' },
];

/* ------------------------------------------------------------------ */
/*  Component                                                         */
/* ------------------------------------------------------------------ */
/** Derive SWA environment name from the current hostname */
function resolveEnvironment(): string {
  const host = window.location.hostname;
  if (host === 'localhost' || host === '127.0.0.1') return 'local';
  if (host.includes('zealous-forest')) return 'production';
  if (host.includes('gentle-smoke')) return 'dev';
  if (host.includes('mango-hill')) return 'dev2';
  return host; // fallback: just show hostname
}

export default function FeedbackForm({ onClose, onSuccess, recentMessages }: FeedbackFormProps) {
  const { t } = useTranslation('modals');
  const [category, setCategory] = useState<FeedbackCategory>('bug');
  const [message, setMessage] = useState('');
  const [screenshot, setScreenshot] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<{ ok: boolean; text: string } | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const dropZoneRef = useRef<HTMLDivElement>(null);
  const [usageSnapshot, setUsageSnapshot] = useState<Record<string, unknown> | null>(null);
  const [autoScreenshot, setAutoScreenshot] = useState(false);
  const overlayRef = useRef<HTMLDivElement>(null);

  // Close on ESC key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  // Fetch usage snapshot when form opens (best-effort)
  useEffect(() => {
    (async () => {
      try {
        const headers = await getAuthHeaders();
        const res = await fetch(`${getApiBaseUrl()}/usageSummary`, { headers });
        if (res.ok) setUsageSnapshot(await res.json());
      } catch { /* non-critical */ }
    })();
  }, []);

  // Auto-capture the page behind the modal AFTER mount (deferred so popup opens instantly)
  useEffect(() => {
    let cancelled = false;
    // Schedule capture with requestIdleCallback (or setTimeout fallback) so
    // the modal renders immediately without waiting for html2canvas.
    const scheduleCapture = (typeof window.requestIdleCallback === 'function')
      ? window.requestIdleCallback
      : (cb: () => void) => window.setTimeout(cb, 300);

    scheduleCapture(async () => {
      if (cancelled) return;
      try {
        // Use ignoreElements to exclude the modal overlay from the capture
        // instead of toggling visibility (which causes a visible flash).
        const overlay = overlayRef.current;

        const canvas = await html2canvas(document.body, {
          useCORS: true,
          allowTaint: true,
          scale: window.devicePixelRatio > 1 ? 1 : window.devicePixelRatio,
          logging: false,
          ignoreElements: (el) => el === overlay,
        });

        if (cancelled) return;

        // Convert to JPEG for smaller size, fall back if too large
        let dataUrl = canvas.toDataURL('image/jpeg', 0.6);
        if (dataUrl.length > 512 * 1024) {
          dataUrl = canvas.toDataURL('image/jpeg', 0.3);
        }
        if (dataUrl.length > 512 * 1024) return; // still too large — skip

        // Only set if user hasn't already pasted/uploaded their own
        setScreenshot(prev => {
          if (prev) return prev; // user already provided one
          setAutoScreenshot(true);
          return dataUrl;
        });
      } catch { /* non-critical — manual screenshot still available */ }
    });
    return () => { cancelled = true; };
  }, []);

  // Capture device info once (enriched with environment & auth context)
  const user = auth.currentUser;
  const deviceInfo = useRef({
    userAgent: navigator.userAgent,
    language: navigator.language,
    languages: [...(navigator.languages || [])],
    platform: navigator.platform,
    screenWidth: window.screen.width,
    screenHeight: window.screen.height,
    viewportWidth: window.innerWidth,
    viewportHeight: window.innerHeight,
    url: window.location.href,
    currentRoute: window.location.pathname,
    timestamp: new Date().toISOString(),
    appVersion: typeof __APP_VERSION__ !== 'undefined' ? __APP_VERSION__ : 'unknown',
    gitCommit: typeof __GIT_COMMIT__ !== 'undefined' ? __GIT_COMMIT__ : 'unknown',
    buildTimestamp: import.meta.env.VITE_BUILD_TIMESTAMP || 'unknown',
    theme: window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light',
    online: navigator.onLine,
    connectionType: (navigator as unknown as { connection?: { effectiveType?: string } }).connection?.effectiveType || 'unknown',
    // New enriched fields
    environment: resolveEnvironment(),
    apiBaseUrl: getApiBaseUrl(),
    authProvider: user?.providerData?.[0]?.providerId || 'unknown',
    accountCreated: user?.metadata?.creationTime || null,
    locale: Intl.DateTimeFormat().resolvedOptions().locale || navigator.language,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'unknown',
  });

  /* ---------- Screenshot handling ---------- */
  const handleFile = useCallback((file: File) => {
    if (!file.type.startsWith('image/')) return;
    if (file.size > 512 * 1024) {
      setResult({ ok: false, text: t('feedback.screenshotTooLarge') });
      return;
    }
    const reader = new FileReader();
    reader.onload = () => setScreenshot(reader.result as string);
    reader.readAsDataURL(file);
  }, [t]);

  // Paste handler (Ctrl+V screenshot)
  useEffect(() => {
    const handlePaste = (e: ClipboardEvent) => {
      const items = e.clipboardData?.items;
      if (!items) return;
      for (const item of items) {
        if (item.type.startsWith('image/')) {
          const file = item.getAsFile();
          if (file) handleFile(file);
          break;
        }
      }
    };
    document.addEventListener('paste', handlePaste);
    return () => document.removeEventListener('paste', handlePaste);
  }, [handleFile]);

  /* ---------- Submit ---------- */
  const handleSubmit = async () => {
    if (!message.trim()) return;
    if (!auth.currentUser) {
      setResult({ ok: false, text: t('feedback.authRequired') });
      return;
    }
    setSubmitting(true);
    setResult(null);

    try {
      const headers = await getAuthHeaders();
      const res = await fetch(`${getApiBaseUrl()}/submitFeedback`, {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          category,
          message: message.trim(),
          screenshot,
          consoleLogs: [...logBuffer],
          recentApiCalls: [...apiCallBuffer],
          usageSnapshot,
          recentMessages: recentMessages || [],
          deviceInfo: deviceInfo.current,
        }),
      });

      if (res.ok) {
        const data = await res.json();
        const bonusMsg = data.bonus?.granted
          ? ` 🎉 +${data.bonus.bonusAdded} ${t('feedback.bonusQueries')}!`
          : '';
        setResult({ ok: true, text: t('feedback.success') + bonusMsg });
        setTimeout(() => { onSuccess?.(); onClose(); }, bonusMsg ? 2500 : 1500);
      } else {
        setResult({ ok: false, text: t('feedback.error') });
      }
    } catch {
      setResult({ ok: false, text: t('feedback.error') });
    } finally {
      setSubmitting(false);
    }
  };

  /* ---------- Drag & drop ---------- */
  const onDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file) handleFile(file);
  }, [handleFile]);

  /* ---------- Render ---------- */
  return (
    <div ref={overlayRef} style={styles.overlay} onClick={onClose}>
      <div
        style={styles.modal}
        role="dialog"
        aria-modal="true"
        aria-labelledby="feedback-modal-title"
        data-testid="feedback-form"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div style={styles.header}>
          <h3 id="feedback-modal-title" style={styles.title}>{t('feedback.title')}</h3>
          <button style={styles.closeBtn} onClick={onClose} aria-label={t('feedback.closeButton')}>×</button>
        </div>

        <div style={styles.body}>
          {/* Category pills */}
          <div style={styles.categoryRow}>
            {CATEGORIES.map((c) => (
              <button
                key={c.value}
                onClick={() => setCategory(c.value)}
                style={{
                  ...styles.pill,
                  ...(category === c.value ? styles.pillActive : {}),
                }}
              >
                {c.icon} {t(c.labelKey)}
              </button>
            ))}
          </div>

          {/* Message */}
          <textarea
            placeholder={t('feedback.placeholder')}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            maxLength={5000}
            rows={5}
            style={styles.textarea}
            autoFocus
          />

          {/* Screenshot area */}
          <div
            ref={dropZoneRef}
            onDrop={onDrop}
            onDragOver={(e) => e.preventDefault()}
            style={styles.screenshotArea}
          >
            {screenshot ? (
              <div style={styles.previewWrap}>
                <img src={screenshot} alt="screenshot" style={styles.previewImg} />
                <button style={styles.removeBtn} onClick={() => { setScreenshot(null); setAutoScreenshot(false); }}>✕</button>
                {autoScreenshot && (
                  <div style={styles.autoLabel}>{t('feedback.autoScreenshot', 'Auto-captured — remove if unwanted')}</div>
                )}
              </div>
            ) : (
              <div
                style={styles.dropZone}
                onClick={() => fileRef.current?.click()}
              >
                <span style={styles.dropIcon}>📷</span>
                <span style={styles.dropText}>{t('feedback.screenshotHint')}</span>
              </div>
            )}
            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              style={{ display: 'none' }}
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) handleFile(f);
              }}
            />
          </div>

          {/* Auto-captured info badge */}
          <div style={styles.infoBadge}>
            ℹ️ {t('feedback.autoCapture', { count: logBuffer.length })}
          </div>

          {/* Result message */}
          {result && (
            <div style={{ ...styles.resultMsg, color: result.ok ? '#059669' : '#dc2626' }}>
              {result.text}
            </div>
          )}

          {/* Submit button */}
          <button
            style={{
              ...styles.submitBtn,
              opacity: (!message.trim() || submitting) ? 0.5 : 1,
            }}
            disabled={!message.trim() || submitting}
            onClick={handleSubmit}
          >
            {submitting ? t('feedback.sending') : t('feedback.submit')}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Styles (inline to avoid CSS coupling)                             */
/* ------------------------------------------------------------------ */
const styles: Record<string, React.CSSProperties> = {
  overlay: {
    position: 'fixed', inset: 0,
    backgroundColor: 'rgba(0,0,0,0.5)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    zIndex: 10000,
    padding: '16px',
  },
  modal: {
    background: '#fff', borderRadius: '16px',
    width: '100%', maxWidth: '480px',
    maxHeight: '90vh', overflow: 'hidden',
    display: 'flex', flexDirection: 'column',
    boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
  },
  header: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '16px 20px',
    borderBottom: '1px solid #e5e7eb',
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  },
  title: {
    margin: 0, fontSize: '16px', fontWeight: 600, color: '#fff',
  },
  closeBtn: {
    background: 'rgba(255,255,255,0.2)', border: 'none', color: '#fff',
    fontSize: '20px', width: '32px', height: '32px', borderRadius: '50%',
    cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
  },
  body: {
    padding: '16px 20px 20px', overflowY: 'auto', flex: 1,
  },
  categoryRow: {
    display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '12px',
  },
  pill: {
    padding: '6px 12px', borderRadius: '20px',
    border: '1px solid #e5e7eb', background: '#f9fafb',
    fontSize: '12px', cursor: 'pointer', whiteSpace: 'nowrap' as const,
    transition: 'all 0.15s',
  },
  pillActive: {
    background: '#667eea', color: '#fff', borderColor: '#667eea',
  },
  textarea: {
    width: '100%', padding: '10px 12px', borderRadius: '8px',
    border: '1px solid #d1d5db', fontSize: '14px', resize: 'vertical' as const,
    fontFamily: 'inherit', boxSizing: 'border-box' as const,
    minHeight: '100px',
  },
  screenshotArea: {
    marginTop: '12px',
  },
  dropZone: {
    border: '2px dashed #d1d5db', borderRadius: '8px',
    padding: '16px', textAlign: 'center' as const, cursor: 'pointer',
    transition: 'border-color 0.15s',
  },
  dropIcon: { fontSize: '24px', display: 'block', marginBottom: '4px' },
  dropText: { fontSize: '12px', color: '#9ca3af' },
  previewWrap: { position: 'relative' as const, display: 'inline-block' },
  previewImg: {
    maxWidth: '100%', maxHeight: '150px', borderRadius: '8px',
    border: '1px solid #e5e7eb',
  },
  removeBtn: {
    position: 'absolute' as const, top: '-8px', right: '-8px',
    background: '#ef4444', color: '#fff', border: 'none',
    borderRadius: '50%', width: '24px', height: '24px',
    cursor: 'pointer', fontSize: '12px',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
  },
  autoLabel: {
    marginTop: '4px', fontSize: '11px', color: '#6b7280', fontStyle: 'italic' as const,
  },
  infoBadge: {
    marginTop: '10px', padding: '8px 12px',
    background: '#f0fdf4', border: '1px solid #bbf7d0',
    borderRadius: '6px', fontSize: '12px', color: '#166534',
  },
  resultMsg: {
    marginTop: '10px', fontSize: '13px', fontWeight: 500, textAlign: 'center' as const,
  },
  submitBtn: {
    marginTop: '14px', width: '100%', padding: '12px',
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    color: '#fff', border: 'none', borderRadius: '10px',
    fontSize: '14px', fontWeight: 600, cursor: 'pointer',
  },
};
